/**
 * optionSearch.js — Shared search utility for any "searchable selector"
 * (Add-Ad category picker, country/city pickers, future filter sheets).
 *
 * Why this exists
 * ---------------
 * Before this file, `SelectSheet` filtered options by the *currently
 * displayed* label only. So an EN user typing "phone" never matched
 * "Electronics & Accessories" (no "phone" in the label), and an AR
 * user typing "هاتف" never matched "الإلكترونيات والإكسسوارات".
 * Users had to scroll manually — bad UX.
 *
 * What it does
 * ------------
 * 1. Builds a single normalized "haystack" string per option from:
 *    - label_en + label_ar (or whatever fields the caller declares)
 *    - aliases.en + aliases.ar (free-form keyword arrays on the option)
 * 2. Normalizes both haystack and query with the same rules
 *    (lowercase, strip Arabic harakat + Latin diacritics, drop
 *    punctuation, collapse whitespace).
 * 3. Substring match → partial words like "pho", "mob", "ذه" all hit.
 *
 * Pure, side-effect-free, no i18n imports — safe to use anywhere.
 */

// Arabic harakat (tashkeel) + tatweel + alif madda + dagger alif. Stripping
// these makes "هاتفٌ" / "هَاتِف" / "هاتف" all collapse to the same form.
const ARABIC_DIACRITICS_RE = /[\u064B-\u065F\u0670\u0640]/g;
const LATIN_DIACRITICS_RE = /[\u0300-\u036f]/g;
const PUNCT_OR_SYMBOL_RE = /[^\p{L}\p{N}\s]+/gu;
const WHITESPACE_RE = /\s+/g;

/** Normalize a string for case-/diacritic-/punctuation-insensitive matching. */
export function normalizeForSearch(s) {
  if (s === null || s === undefined) return '';
  return String(s)
    .toLowerCase()
    .normalize('NFD')
    .replace(ARABIC_DIACRITICS_RE, '')
    .replace(LATIN_DIACRITICS_RE, '')
    .replace(/[إأآا]/g, 'ا')   // unify alef variants
    .replace(/ى/g, 'ي')         // unify yaa
    .replace(/ة/g, 'ه')         // taa marbuta → haa (forgiving)
    .replace(PUNCT_OR_SYMBOL_RE, ' ')
    .replace(WHITESPACE_RE, ' ')
    .trim();
}

/**
 * Build a flat searchable haystack for a single option. Pulls from:
 *   - opt.label              (current-language label string the UI shows)
 *   - opt.label_en, opt.label_ar
 *   - opt.aliases (string | string[] | { en: [...], ar: [...] })
 *   - opt.value               (slug — last resort)
 *
 * Caller may also pass `extraGetters` to extract custom fields
 * (e.g. `(opt) => opt.tags.join(' ')`).
 */
export function buildOptionHaystack(opt, extraGetters = []) {
  if (!opt || typeof opt !== 'object') return '';
  const parts = [];

  if (opt.label) parts.push(opt.label);
  if (opt.label_en) parts.push(opt.label_en);
  if (opt.label_ar) parts.push(opt.label_ar);
  // Handful of options use `name_en` / `name_ar` (countries / cities).
  if (opt.name_en) parts.push(opt.name_en);
  if (opt.name_ar) parts.push(opt.name_ar);

  const a = opt.aliases;
  if (a) {
    if (typeof a === 'string') parts.push(a);
    else if (Array.isArray(a)) parts.push(...a);
    else if (typeof a === 'object') {
      if (Array.isArray(a.en)) parts.push(...a.en);
      if (Array.isArray(a.ar)) parts.push(...a.ar);
      // any extra language buckets
      for (const k of Object.keys(a)) {
        if (k === 'en' || k === 'ar') continue;
        const v = a[k];
        if (Array.isArray(v)) parts.push(...v);
        else if (typeof v === 'string') parts.push(v);
      }
    }
  }

  if (opt.value) parts.push(String(opt.value));

  for (const g of extraGetters) {
    try {
      const r = g(opt);
      if (r) parts.push(String(r));
    } catch { /* ignore getter failures */ }
  }

  return normalizeForSearch(parts.join(' '));
}

/**
 * Returns true when `option` matches `query`.
 *
 * Multi-token query: split on whitespace, ALL tokens must appear as
 * substrings of the haystack (AND semantics). This is friendlier than
 * a single-substring check ("real estate" → "Real Estate" works
 * because both tokens appear) yet still permissive on partial words
 * ("pho mob" → both substrings of "phone mobile..." haystack).
 */
export function optionMatchesQuery(option, query, extraGetters = []) {
  const q = normalizeForSearch(query);
  if (!q) return true; // empty query → no filter
  const haystack = buildOptionHaystack(option, extraGetters);
  if (!haystack) return false;

  // Split by whitespace and require every token to be a substring.
  const tokens = q.split(' ').filter(Boolean);
  for (const tok of tokens) {
    if (!haystack.includes(tok)) return false;
  }
  return true;
}

// =============================================================================
// Relevance ranking
// =============================================================================
//
// `filterOptionsByQuery` keeps the input order; that's wrong for a category
// picker where typing "mobile" should put **Mobile & Accessories** above
// **Electronics & Accessories** (both contain "mobile" as an alias, but only
// the first has it in its visible LABEL — that's the user's primary intent).
//
// `rankOptionsByQuery` scores every option against the query across multiple
// fields (labels EN/AR, then aliases EN/AR/etc.) and sorts results by
// descending score with stable secondary order. Caller decides whether to
// drop zero-score options (we do).
//
// Score tiers (per matched field, take MAX across fields):
//   label exact        → 200
//   label starts-with  → 180
//   label whole-word   → 150
//   label substring    → 130
//   alias exact        → 100
//   alias starts-with  →  80
//   alias whole-word   →  60
//   alias substring    →  30
//
// "Whole word" means surrounded by word boundaries inside the field
// (matters for fields like a label or a multi-word alias phrase).
//
// Tokens within a multi-token query are AND'ed (every token must hit
// somewhere on the option). The option's score is the sum of the best
// per-token score, so multi-word queries still favour the most relevant
// option overall.

/** Field-level score for a single normalized field against a normalized token. */
function _scoreField(field, token, isLabel) {
  if (!field || !token) return 0;
  const exactBase   = isLabel ? 200 : 100;
  const startsBase  = isLabel ? 180 :  80;
  const wordBase    = isLabel ? 150 :  60;
  const subBase     = isLabel ? 130 :  30;

  if (field === token) return exactBase;
  if (field.startsWith(token + ' ') || field.startsWith(token)) return startsBase;
  // whole-word match: token surrounded by spaces or boundaries
  if ((' ' + field + ' ').includes(' ' + token + ' ')) return wordBase;
  if (field.includes(token)) return subBase;
  return 0;
}

function _collectFields(opt) {
  const labels = [];
  const aliases = [];
  if (opt.label)    labels.push(normalizeForSearch(opt.label));
  if (opt.label_en) labels.push(normalizeForSearch(opt.label_en));
  if (opt.label_ar) labels.push(normalizeForSearch(opt.label_ar));
  if (opt.name_en)  labels.push(normalizeForSearch(opt.name_en));
  if (opt.name_ar)  labels.push(normalizeForSearch(opt.name_ar));

  const a = opt.aliases;
  if (a) {
    if (typeof a === 'string') aliases.push(normalizeForSearch(a));
    else if (Array.isArray(a)) for (const x of a) aliases.push(normalizeForSearch(x));
    else if (typeof a === 'object') {
      for (const k of Object.keys(a)) {
        const v = a[k];
        if (Array.isArray(v)) for (const x of v) aliases.push(normalizeForSearch(x));
        else if (typeof v === 'string') aliases.push(normalizeForSearch(v));
      }
    }
  }
  return { labels: labels.filter(Boolean), aliases: aliases.filter(Boolean) };
}

/** Score a single option against the query. 0 = no match. */
export function scoreOption(option, query, extraGetters = []) {
  const q = normalizeForSearch(query);
  if (!q) return 1; // empty query → everything passes with neutral score
  if (!option || typeof option !== 'object') return 0;

  const { labels, aliases } = _collectFields(option);
  const extra = [];
  for (const g of extraGetters) {
    try {
      const r = g(option);
      if (r) extra.push(normalizeForSearch(String(r)));
    } catch { /* ignore */ }
  }

  const tokens = q.split(' ').filter(Boolean);
  let total = 0;
  for (const tok of tokens) {
    let best = 0;
    for (const f of labels)  best = Math.max(best, _scoreField(f, tok, true));
    for (const f of extra)   best = Math.max(best, _scoreField(f, tok, true));
    for (const f of aliases) best = Math.max(best, _scoreField(f, tok, false));
    if (best === 0) return 0; // AND semantics — every token must score
    total += best;
  }
  return total;
}

/**
 * Rank-then-filter: returns options sorted by descending relevance, keeping
 * only those with score > 0. Stable secondary order preserves original
 * input order for ties.
 */
export function rankOptionsByQuery(options, query, extraGetters = []) {
  if (!Array.isArray(options) || options.length === 0) return options || [];
  const q = normalizeForSearch(query);
  if (!q) return options;
  const scored = options.map((opt, idx) => ({
    opt,
    idx,
    score: scoreOption(opt, query, extraGetters),
  }));
  return scored
    .filter((s) => s.score > 0)
    .sort((a, b) => (b.score - a.score) || (a.idx - b.idx))
    .map((s) => s.opt);
}

/**
 * Filter an array of options by query. Stable order.
 * Empty / whitespace-only query returns the input unchanged.
 *
 * NOTE: kept for backwards compatibility — for any selector where
 * relevance ordering matters (category picker), prefer
 * `rankOptionsByQuery`.
 */
export function filterOptionsByQuery(options, query, extraGetters = []) {
  if (!Array.isArray(options) || options.length === 0) return options || [];
  const q = normalizeForSearch(query);
  if (!q) return options;
  return options.filter((opt) => optionMatchesQuery(opt, query, extraGetters));
}
